import { defineStore } from 'pinia';
import { httpAPI } from '@/services/ajax';
import app from '@/main.js';
import { useUserStore } from './user';

export const useEmployeesStore = defineStore('employees', {
    state: () => ({
        employees: [],
        consultants: [], // separate list of consultants for admin context
        fetchingConsultants: false, // are we currently fetching consultants?
        fetchingConsultantsPromise: null, // promise for ongoing consultants fetch

        fetching: false, // are we currently fetching the user?
        callbackBus: {}, // functions to call once fetch is done
        companyOwnerId: null, // company owner ID for site filtering
    }),
    actions: {
        _fetchAndTriggerCallbacks() {
            /** Force fetch the user from the API, then perform all the callbacks in callbackBus */
            if (!app) return;

            this.fetching = true;

            const vm = this;
            const url = app.config.globalProperties.$env.apiRoot + 'user/read';
            const payload = {
                fields: [
                    'first_name',
                    'last_name',
                    'email',
                    'profile_image_id',
                    'hash',
                    'phone',
                    'city',
                    'hire_date',
                    'title',
                    'is_company_admin',
                    'is_wp_connected',
                    'invite_accepted_at',
                    'deactivated_at',
                    'created_at',
                ],
                include: {
                    sites: {
                        fields: ['id', 'name'],
                    },
                    roles: {
                        fields: ['id', 'name'],
                    },
                    key_contacts: true,
                },
            };

            httpAPI(url, payload, 'GET', app.config.globalProperties.$router)
                .then((response) => {
                    if (response?.success && response.data) {
                        Object.freeze(response.data);
                        vm.employees = response.data;
                    }
                    this.fetching = false;
                })
                .catch(() => {
                    this.fetching = false;
                })
                .finally(() => {
                    this.fetching = false;

                    // Perform list of callback functions
                    for (const callbackId in this.callbackBus) {
                        const callback = this.callbackBus[callbackId];
                        try {
                            callback(this.employees);
                        } catch (error) {
                            console.error(
                                'Error in employees store callback',
                                error
                            );
                        }
                        delete this.callbackBus[callbackId]; // Remove it from bus
                    }
                });
        },

        fetchEmployees(callback = null) {
            /** Fetch user from API and perform the given callback once completed */

            // Add callback to our bus
            if (callback) {
                const callbackIds = Object.keys(this.callbackBus);
                const lastId = callbackIds.length
                    ? callbackIds[callbackIds.length - 1]
                    : 0;
                this.callbackBus[lastId + 1] = callback;
            }

            // Only start resyncing if not already resyncing
            if (!this.fetching) this._fetchAndTriggerCallbacks();
        },

        sync(callback = null) {
            /** Fetch the user from the API (if necessary), then perform the callback function once completed */
            if (!this.employees.length) this.fetchEmployees(callback);
            else if (callback) callback(this.employees);
        },

        async fetchCompanyOwner(companyId) {
            /** Fetch company owner ID for site filtering in admin context */
            if (!companyId || !app) return;

            const url =
                app.config.globalProperties.$env.apiRoot + 'company/read';
            const payload = {
                where: { id: companyId },
                fields: ['admin_id'],
            };

            try {
                const response = await httpAPI(
                    url,
                    payload,
                    'GET',
                    app.config.globalProperties.$router
                );
                if (response?.success && response.data?.[0]) {
                    this.companyOwnerId = response.data[0].admin_id;
                }
            } catch (error) {
                console.error('Failed to fetch company owner:', error);
            }
        },

        fetchConsultants() {
            if (!app) return Promise.resolve(null);
            if (this.fetchingConsultantsPromise)
                return this.fetchingConsultantsPromise;

            this.fetchingConsultants = true;

            const url =
                app.config.globalProperties.$env.apiRoot +
                'user/consultant/read';

            this.fetchingConsultantsPromise = httpAPI(
                url,
                {},
                'GET',
                app.config.globalProperties.$router
            )
                .then((response) => {
                    if (response?.success && response.data) {
                        Object.freeze(response.data);
                        this.consultants = response.data;
                    }
                    return this.consultants;
                })
                .catch((error) => {
                    console.error('Failed to fetch consultants:', error);
                    return Promise.resolve(null);
                })
                .finally(() => {
                    this.fetchingConsultants = false;
                    this.fetchingConsultantsPromise = null;
                });

            return this.fetchingConsultantsPromise;
        },

        syncConsultants() {
            if (this.consultants?.length) {
                return Promise.resolve(this.consultants);
            }
            return this.fetchConsultants();
        },

        clear() {
            this.employees = [];
            this.fetching = false;
            this.callbackBus = {};
            this.companyOwnerId = null;
        },

        getEmployeeById(id) {
            return this.employees.find((emp) => emp.id == id || emp.hash == id);
        },

        _getEmployeeName(emp) {
            const nameTokens = [];
            if (emp.first_name) nameTokens.push(emp.first_name);
            if (emp.last_name) nameTokens.push(emp.last_name);
            return nameTokens.join(' ');
        },

        _toEmployeeOption(emp, format = 'string') {
            const name = this._getEmployeeName(emp);
            const value = format === 'number' ? Number(emp.id) : String(emp.id);

            return { text: name, value };
        },

        _sortEmployeeOptions(options = []) {
            return [...options].sort((a, b) =>
                String(a?.text || '').localeCompare(String(b?.text || ''), undefined, {
                    sensitivity: 'base',
                })
            );
        },

        _filterEmployees(employees = [], ignoreIds = [], options = {}) {
            const ignoreDeactivated =
                typeof options.ignoreDeactivated === 'boolean'
                    ? options.ignoreDeactivated
                    : options.ignoreDeactivates ?? true;
            const ignoreInvited = options.ignoreInvited ?? true;
            const ignoredEmployeeIds = new Set(
                ignoreIds.map((id) => String(id))
            );

            return employees
                .filter((emp) => !ignoredEmployeeIds.has(String(emp.id)))
                .filter((emp) =>
                    ignoreDeactivated ? !emp.deactivated_at : true
                )
                .filter((emp) =>
                    ignoreInvited ? emp.invite_accepted_at : true
                );
        },

        _hasRole(emp, roleId) {
            return (
                emp.roles &&
                Array.isArray(emp.roles) &&
                emp.roles.some((role) => String(role.id) === String(roleId))
            );
        },

        getEmployeeOptions(
            ignoreIds = [],
            { ignoreDeactivated = true, ignoreInvited = true } = {}
        ) {
            const options = this._filterEmployees(this.employees, ignoreIds, {
                ignoreDeactivated,
                ignoreInvited,
            }).map((emp) => this._toEmployeeOption(emp, 'string'));
            return this._sortEmployeeOptions(options);
        },

        /**
         * Get employees filtered by role ID (optionally scoped to site IDs)
         * @param {string|number} roleId - The role ID to filter by
         * @param {Array} siteIds - Optional site IDs to scope role results
         * @returns {Array} Array of employee objects with the specified role
         */
        getEmployeesByRole(roleId, siteIds = []) {
            if (!roleId) return [];

            const siteScopedEmployees = siteIds.length
                ? this.getEmployeesBySites(siteIds)
                : this.employees;

            return siteScopedEmployees.filter((emp) =>
                this._hasRole(emp, roleId)
            );
        },

        /**
         * Get employee IDs filtered by role ID (optionally scoped to site IDs)
         * @param {string|number} roleId - The role ID to filter by
         * @param {Array} siteIds - Optional site IDs to scope role results
         * @returns {Array} Array of employee IDs with the specified role
         */
        getEmployeesIdByRole(roleId, siteIds = []) {
            return this.getEmployeesByRole(roleId, siteIds).map((emp) => emp.id);
        },

        /**
         * Get employee options (text/value format) filtered by role
         * @param {string|number} roleId - The role ID to filter by
         * @param {string} format - Return format: 'string' or 'number' for value type
         * @param {Array} siteIds - Optional site IDs to scope role results
         * @param {Array} ignoreIds - Employee IDs to exclude
         * @param {Object} options - { ignoreDeactivated: true, ignoreInvited: true }
         * @returns {Array} Array of {text, value} objects
         */
        getEmployeeOptionsByRole(
            roleId,
            format = 'string',
            siteIds = [],
            ignoreIds = [],
            options = {}
        ) {
            if (!roleId) return [];

            const employees = this.getEmployeesByRole(roleId, siteIds);

            const employeeOptions = this._filterEmployees(employees, ignoreIds, options).map(
                (emp) => this._toEmployeeOption(emp, format)
            );
            return this._sortEmployeeOptions(employeeOptions);
        },

        /**
         * Filter employees by site IDs
         * @param {Array} siteIds - Array of site IDs (strings or numbers)
         * @returns {Array} - Employee objects that belong to ANY of the provided sites
         */
        getEmployeesBySites(siteIds = []) {
            if (!siteIds.length) return [];
            // Normalize to strings for consistent comparison
            const normalizedSiteIds = new Set(siteIds.map((id) => String(id)));
            return this.employees.filter((emp) => {
                // Include company owner regardless of site assignments
                if (
                    this.effectiveCompanyOwnerId &&
                    String(emp.id) === String(this.effectiveCompanyOwnerId)
                ) {
                    return true;
                }
                // Regular site filtering for other employees
                return emp.sites?.some((site) =>
                    normalizedSiteIds.has(String(site.id))
                );
            });
        },

        /**
         * Get employee options (for dropdowns) filtered by site IDs
         * @param {Array} siteIds - Array of site IDs to filter by
         * @param {Array} ignoreIds - Employee IDs to exclude
         * @param {Object} options - { ignoreDeactivated: true, ignoreInvited: true }
         * @returns {Array} - Array of { text, value } objects for dropdowns
         */
        getEmployeeOptionsBySites(siteIds = [], ignoreIds = [], options = {}) {
            const employees = this.getEmployeesBySites(siteIds);

            const employeeOptions = this._filterEmployees(employees, ignoreIds, options).map(
                (emp) => this._toEmployeeOption(emp, 'number')
            );
            return this._sortEmployeeOptions(employeeOptions);
        },
    },

    getters: {
        /**
         * Get effective company owner ID for both admin and regular context
         * @returns {number|null} - Company owner ID
         */
        effectiveCompanyOwnerId(state) {
            // Use stored companyOwnerId if it set through admin context or fallback to userStore
            if (state.companyOwnerId) return state.companyOwnerId;

            // Access userStore for non-admin context
            const userStore = useUserStore();
            return userStore.company?.admin_id || null;
        },

        getConsultantOptions(state) {
            return state.consultants.map((consultant) => ({
                text: `${consultant.first_name} ${consultant.last_name}`,
                value: String(consultant.id),
            }));
        },
    },
});
